import React, { useEffect, useState } from 'react';

import { StockInfo } from '@ndvr/market-data-api';
import { RestrictionType, TradeRestrictionAsset } from '@ndvr/portfolio-api';

import { AssetClass } from '@/cip/_openapi/portfolio-optimizer';
import { TickerSelectorInput } from '@/components/TickerSelectorInput';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';

export interface StockRestriction {
    stock: StockInfo;
    restricted: boolean;
    doNotSell?: boolean;
}

interface EditRestrictionModalProps {
    preferences: TradeRestrictionAsset[];
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (payload: StockRestriction) => void;
    editingStock?: StockRestriction | null;
}

export const EditRestrictionModal = ({
    isOpen,
    onClose,
    onSubmit,
    editingStock,
    preferences,
}: EditRestrictionModalProps) => {
    const [selectedSecurity, setSelectedSecurity] = useState<StockInfo | null>(null);
    const [doNotBuy, setDoNotBuy] = useState(false);
    const [doNotSell, setDoNotSell] = useState(false);

    // Initialize form when editing a stock
    useEffect(() => {
        if (editingStock && isOpen) {
            setSelectedSecurity(editingStock.stock);
            setDoNotBuy(editingStock.restricted);
            setDoNotSell(editingStock.doNotSell || false);
        }
    }, [editingStock, isOpen]);

    const getPreferenceText = () => {
        if (doNotBuy && doNotSell) {
            return 'Do not Buy or Sell';
        } else if (doNotBuy) {
            return 'Do not Buy';
        } else if (doNotSell) {
            return 'Do not Sell';
        } else {
            return '--';
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedSecurity) {
            return;
        }

        onSubmit({
            stock: selectedSecurity,
            restricted: doNotBuy,
            doNotSell: doNotSell,
        });

        // Reset form
        setSelectedSecurity(null);
        setDoNotBuy(false);
        setDoNotSell(false);
        onClose();
    };

    const handleClose = () => {
        setSelectedSecurity(null);
        setDoNotBuy(false);
        setDoNotSell(false);
        onClose();
    };

    const handleTickerSelect = (security: StockInfo) => {
        setSelectedSecurity(security);
    };

    const getRestrictionType = (restrictionType: RestrictionType) => {
        if (restrictionType === RestrictionType.DO_NOT_BUY) {
            return 'Do not Buy';
        } else if (restrictionType === RestrictionType.DO_NOT_SELL) {
            return 'Do not Sell';
        } else if (restrictionType === RestrictionType.DO_NOT_BUY_OR_SELL) {
            return 'Do not Buy or Sell';
        } else if (restrictionType === RestrictionType.INSIDER) {
            return 'Do not Buy or Sell';
        }
        return '--';
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="font-halyard sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-halyard">
                        {editingStock ? 'Edit Restriction' : 'Add Restriction'}
                    </DialogTitle>
                    <DialogDescription></DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-4">
                        <div className="rounded-md bg-gray-100 p-4">
                            <div className="mb-4 grid grid-cols-2 gap-4 text-sm text-gray-600">
                                <div>
                                    <span className="font-medium">TICKER</span>
                                    <div className="text-lg">{selectedSecurity?.tickerSymbol || '--'}</div>
                                </div>
                                <div>
                                    <span className="font-medium">Preference</span>
                                    <div className="text-lg">{getPreferenceText()}</div>
                                </div>
                            </div>
                        </div>

                        <TickerSelectorInput
                            onSelectTicker={(asset) => {
                                const security: StockInfo = {
                                    shareId: asset.sId,
                                    shareClassId: asset.scId,
                                    tickerSymbol: asset.symbol || '',
                                    companyName: asset.description || '',
                                    marketCap: '',
                                    marketCapWeight: '',
                                };
                                handleTickerSelect(security);
                            }}
                            value={
                                selectedSecurity
                                    ? `${selectedSecurity.tickerSymbol} - ${selectedSecurity.companyName}`
                                    : ''
                            }
                            disabled={!!editingStock}
                            label="Enter a Ticker or Company Name"
                            placeholder="Search by ticker or company name..."
                            assetClass={AssetClass.EQUITY}
                            excludeItems={
                                preferences?.map((p) => ({
                                    sId: p.shareId,
                                    scId: p.shareClassId,
                                    displayText: getRestrictionType(p.restrictionType),
                                })) || []
                            }
                        />

                        <div className="space-y-4 pt-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-halyard font-medium">Do Not Buy</h3>
                                    <p className="font-halyard text-sm text-gray-600">
                                        Do not buy this stock in my portfolio
                                    </p>
                                </div>
                                <Switch checked={doNotBuy} onCheckedChange={setDoNotBuy} />
                            </div>

                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-halyard font-medium">Do Not Sell</h3>
                                    <p className="font-halyard text-sm text-gray-600">
                                        Do not sell this stock in my portfolio
                                    </p>
                                </div>
                                <Switch checked={doNotSell} onCheckedChange={setDoNotSell} />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={handleClose} className="font-halyard">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="hover:bg-opacity-90 font-halyard text-white"
                            style={{ backgroundColor: 'rgb(125, 85, 43)' }}
                            disabled={!selectedSecurity || (!doNotBuy && !doNotSell)}
                        >
                            {editingStock ? 'Update' : 'Add'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};
